import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Typography from '@repo/ui/typography'

import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { TripBookingProperties } from '../../../../lib/types/models/trip-booking/properties.trip-booking'
import { TripBookingTypes } from '../../../../lib/types/models/trip-booking/trip-booking'
import TransportPicto from '../../../elements/transport-picto'

type InfoType = 'default' | 'reserved'

interface Props {
  tripBooking: TripBookingTypes.Light
  displayFormat?: 'default' | 'compact'
  lightTrip?: boolean
  infoType?: InfoType
}

type TripbookingInfo = {
  studentsInfo: string
  staffInfo: string
}

function getTripbookingInfo(
  tripBooking?: TripBookingTypes.Light
): Record<InfoType, TripbookingInfo> | undefined {
  if (!tripBooking) return undefined

  const studentsInfo = `# Jeunes : ${tripBooking?.numberOfStudentsPlacesAllocated} inscrits / ${tripBooking?.numberOfStudentsPlacesDispatched} dispatchées`
  const staffInfo = `# Encadrants : ${tripBooking?.numberOfStaffPlacesAllocated} inscrits / ${tripBooking?.numberOfStaffPlacesDispatched} dispatchées`

  const reservedStudentsInfo = `# Jeunes : ${tripBooking?.numberOfStudentsPlacesDispatched} inscrits / ${tripBooking?.numberOfStudentsPlacesReserved} réservées`
  const reservedStaffInfo = `# Encadrants : ${tripBooking?.numberOfStaffPlacesDispatched} inscrits / ${tripBooking?.numberOfStaffPlacesReserved} réservées`

  return {
    default: { studentsInfo, staffInfo },
    reserved: { studentsInfo: reservedStudentsInfo, staffInfo: reservedStaffInfo }
  }
}

const TripBookingBadge = ({
  tripBooking,
  displayFormat = 'default',
  lightTrip = false,
  infoType = 'default'
}: Props) => {
  const { staffInfo, studentsInfo } = getTripbookingInfo(tripBooking)?.[infoType] ?? {}
  const directionalityDetails = TripBookingProperties.getDirectionalityDetails(
    tripBooking.directionality
  )

  const tripLabel = lightTrip
    ? TripProperties.getTripLightLabel(tripBooking.relatedTrip)
    : TripProperties.getTripLabel(tripBooking.relatedTrip)

  if (displayFormat === 'compact') {
    return (
      <Badge
        variant="white"
        color={tailwindColors.white.hover}
        className="p-2 h-fit my-2"
        text={
          <div className="flex items-center gap-2">
            {!tripBooking.isTakeInCharge && tripBooking.transportMeans?.picto && (
              <TransportPicto picto={tripBooking.transportMeans.picto} />
            )}
            <span>{`${tripLabel} | ${studentsInfo} - ${staffInfo}`}</span>
          </div>
        }
      />
    )
  }

  return (
    <Badge
      variant="white"
      color={tailwindColors.white.hover}
      className="p-2 h-fit my-2"
      text={
        <div className="flex flex-col gap-1">
          <div className="flex flex-row gap-1 items-center flex-wrap">
            <Typography
              variant="label-semibold"
              className="w-fit inline"
              color={directionalityDetails?.textColor}
            >
              {directionalityDetails?.label}
            </Typography>
            {!tripBooking.isTakeInCharge && (
              <>
                <Typography variant="label-medium" className="text-grey-500">
                  -
                </Typography>
                <Typography variant="label-medium">{tripBooking.reservationNumber}</Typography>
              </>
            )}
            <Typography variant="label-medium" className="text-grey-500">
              -
            </Typography>
            <Typography variant="label-medium">{tripLabel}</Typography>
          </div>
          <span>{`${studentsInfo} - ${staffInfo}`}</span>
        </div>
      }
    />
  )
}

export default TripBookingBadge
